import React from "react";
import styled from "@emotion/styled";
import { CoveragesCard, ActionLabelCard, Divider, Flex, InsuranceConditions, OfferHeaderCard } from 'src/components';
import { mediaQuery, setOneTimeToggle, useAppDispatch } from "src/common";
import { Card } from "./card";

const CardStyle = styled(Card)({
  padding: '16px 12px',

  [mediaQuery('mobile')]: {
    padding: '20px'
  },
})

interface OfferSummaryCardProps {
  offer: any;
  documents?: Array<{ title: string, src: string }>;
  loading?: boolean;
  oneTimeToggle?: boolean;
  className?: string;
}

const isCoverageValueTrue = (v: unknown) => v === true || v === 'true';

export const OfferSummaryCard = ({ offer, documents = [], oneTimeToggle = false, className }: OfferSummaryCardProps) => {
  const dispatch = useAppDispatch();

  const discountPercentage = ((offer.total.generalTransferWithInstallments - offer.total.generalTransfer) / offer.total.generalTransferWithInstallments) * 100;
  const roundedDiscountPercentage = Math.round(discountPercentage); // Zaokruživanje na više bez decimalnih mjesta

  const supp = offer?.supplementary;
  const coveragesWithValue = (supp?.coverages ?? []) as Array<{ code?: string | null; label?: string | null; value?: unknown } | null>;
  const additionalCoveragesFromSupp = (supp?.additionalCoverages ?? []) as Array<{ code?: string | null; label?: string | null; description?: string | null; editOnWeb?: boolean | null; displayOnWeb?: boolean | null } | null>;

  // Editable coverage codes from additional coverages only (editOnWeb true)
  const editableCodesFromApi = (additionalCoveragesFromSupp ?? []).filter(Boolean).filter((c) => c?.editOnWeb === true).map((c) => c?.code).filter((code): code is string => !!code);
  const editableCodesSet = new Set(editableCodesFromApi);

  // Backend reports selected editable coverages in supplementary.coverages with value "true"
  const selectedCodes = coveragesWithValue
    .filter((c): c is { code: string; label?: string | null; value?: unknown } => !!c && !!c.code && editableCodesSet.has(c.code) && isCoverageValueTrue(c.value))
    .map((c) => c.code as string);

  // Additional coverages with selected state (editable ones can be toggled; selected = from checkout)
  const additionalCoveragesWithSelection = (additionalCoveragesFromSupp ?? []).filter(Boolean).map((c) => ({
    code: c?.code ?? '',
    label: c?.label ?? c?.code ?? '',
    description: c?.description ?? '',
    selected: selectedCodes.includes(c?.code ?? ''),
    editOnWeb: c?.editOnWeb,
    displayOnWeb: c?.displayOnWeb,
  }));

  return (
    <CardStyle radius={12} className={className}>
      <Flex vertical gap={12}>
        <OfferHeaderCard
          brandName={offer.supplementary.brandCode}
          offerLabel={offer.supplementary.label}
          price={oneTimeToggle ? offer.total.generalTransfer : (offer.total.generalTransferWithInstallments / 12)}
          priceType={oneTimeToggle ? 'yearly' : 'monthly'}
          logoUrl={offer.supplementary.logoUrl}
          discountPercentage={roundedDiscountPercentage}
          handlePriceTypeChange={() => dispatch(setOneTimeToggle(!oneTimeToggle))}
        />
        <Divider dashed />
        <ActionLabelCard text={offer.supplementary.actionLabel} />
        <CoveragesCard
          isExpanded={true}
          coverages={offer.supplementary.includedInPrice}
          additionalCoverages={additionalCoveragesWithSelection}
        />
        <Divider dashed />
        <InsuranceConditions documents={documents} />
      </Flex>
    </CardStyle>
  )
};
